import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  createDocumentSkill,
  skillDialogue,
  updateDocumentSkillContent,
} from '@/api/documentWorkbenchApi';
import { formatApiErrorMessage } from '@/api/http';
import { useAiAssistantStore } from '@/stores/useAiAssistantStore';
import { useSmartDocumentAiStore } from '@/stores/useSmartDocumentAiStore';
import type {
  DocumentSkillDetail,
  ParseSkillFromDialogueResult,
  SkillDialogueHistoryItem,
  SkillDialogueMode,
  SmartDocumentSkillDraftStructured,
} from '@/types/documentWorkbench';
import {
  isSmartDocumentScenario,
  resolveSmartDocumentScenario,
} from '@/utils/aiSessionContext';
import { thoughtStepsToJson, type ThoughtStep } from '@/utils/aiThought';

const GUIDE_CREATE =
  '我们来创建一项个人技能。请尽量补充：①名称与适用场景；②输入内容；③处理步骤（SOP）；④期望输出格式；⑤是否需要检索项目资料。'
  + '例如「SOR 协作生成」侧重按章节检索并输出带 Citation 的草稿；你可按自己的场景描述目标。';

const GUIDE_OPTIMIZE =
  '我们来优化当前技能。请说明希望改进的点（例如 Prompt 更具体、增加检查步骤、调整输出格式等），我会基于现有内容给出修订草稿。';

function isCreateSkillIntent(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  return /(?:创建|新建|做一个|帮我做)(?:一个|一项|一份)?(?:Skill|技能|方法)/i.test(t)
    || /(?:Skill|技能).{0,8}(?:创建|新建)/i.test(t)
    || /与\s*AI\s*对话创建技能/.test(t);
}

function isAbortError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const any = e as { code?: string; name?: string; message?: string };
  return any.code === 'ERR_CANCELED'
    || any.name === 'CanceledError'
    || any.name === 'AbortError'
    || /cancel|abort/i.test(String(any.message || ''));
}

export { isCreateSkillIntent };

export const useSmartDocumentSkillAuthorStore = defineStore('smartDocumentSkillAuthor', () => {
  const active = ref(false);
  const mode = ref<SkillDialogueMode>('create');
  const conversationId = ref('');
  const sourceSkillId = ref('');
  const category = ref('document_generation');
  const sending = ref(false);
  const pendingDraft = ref<ParseSkillFromDialogueResult | null>(null);
  const pendingMessageId = ref<string | null>(null);
  const history = ref<SkillDialogueHistoryItem[]>([]);
  const abortController = ref<AbortController | null>(null);

  const boundScenario = ref('');
  const boundSessionId = ref('');

  const aiStore = useAiAssistantStore();
  const smartDocAi = useSmartDocumentAiStore();

  function bindPanelContext(ctx: { scenario: string; sessionId: string }) {
    boundScenario.value = (ctx.scenario || '').trim();
    boundSessionId.value = (ctx.sessionId || '').trim();
  }

  function resolveScenario(): string {
    if (boundScenario.value && isSmartDocumentScenario(boundScenario.value)) {
      return boundScenario.value;
    }
    const scenario = resolveSmartDocumentScenario(smartDocAi.workspaceId || '0');
    if (!isSmartDocumentScenario(aiStore.currentScenario) || aiStore.currentScenario !== scenario) {
      aiStore.currentScenario = scenario;
    }
    return scenario;
  }

  function addAuthorMessage(
    role: 'user' | 'assistant',
    content: string,
    structured?: Record<string, unknown> | null,
    thought?: string,
  ) {
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    if (sessionId) {
      return aiStore.addMessage(scenario, sessionId, role, content, undefined, thought, structured);
    }
    return aiStore.addMessage(scenario, role, content, undefined, thought, structured);
  }

  function buildDraftStructured(
    draft: ParseSkillFromDialogueResult,
    status: SmartDocumentSkillDraftStructured['status'],
  ): SmartDocumentSkillDraftStructured {
    return {
      kind: 'smart_document_skill_draft',
      status,
      mode: mode.value,
      code: draft.code,
      name: draft.name,
      category: draft.category,
      description: draft.description,
      promptText: draft.promptText,
      sopText: draft.sopText,
      inputSchemaJson: draft.inputSchemaJson,
      toolPermissionsJson: draft.toolPermissionsJson,
      outputContractJson: draft.outputContractJson,
      sourceSkillId: sourceSkillId.value || undefined,
      warnings: draft.warnings,
    };
  }

  async function beginSkillAuthoring(payload?: {
    mode?: SkillDialogueMode;
    sourceSkillId?: string;
    category?: string;
  }) {
    mode.value = payload?.mode || 'create';
    sourceSkillId.value = payload?.sourceSkillId || '';
    category.value = payload?.category || 'document_generation';
    conversationId.value = '';
    pendingDraft.value = null;
    pendingMessageId.value = null;
    history.value = [];
    active.value = true;

    if (mode.value === 'optimize' && !sourceSkillId.value && smartDocAi.selectedSkillId) {
      sourceSkillId.value = smartDocAi.selectedSkillId;
    }

    resolveScenario();

    const guide = mode.value === 'optimize' ? GUIDE_OPTIMIZE : GUIDE_CREATE;
    const skillName = smartDocAi.selectedSkillName;
    const content = mode.value === 'optimize' && skillName
      ? `${guide}\n\n当前技能：${skillName}`
      : guide;
    addAuthorMessage('assistant', content);
    history.value.push({ role: 'assistant', content });
  }

  function endSkillAuthoring() {
    active.value = false;
    conversationId.value = '';
    pendingDraft.value = null;
    pendingMessageId.value = null;
    history.value = [];
    sourceSkillId.value = '';
    abortController.value = null;
  }

  function cancelSending() {
    abortController.value?.abort();
    abortController.value = null;
    sending.value = false;
  }

  function patchPendingStructured(status: SmartDocumentSkillDraftStructured['status']) {
    const msgId = pendingMessageId.value;
    if (!msgId || !pendingDraft.value) return;
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    aiStore.updateMessage(
      scenario,
      msgId,
      {
        structuredResult: buildDraftStructured(pendingDraft.value, status) as unknown as Record<string, unknown>,
      },
      sessionId,
    );
  }

  async function sendUserMessage(
    text: string,
    opts?: {
      signal?: AbortSignal;
      onThought?: (steps: ThoughtStep[]) => void;
    },
  ): Promise<{ aborted?: boolean }> {
    const normalized = text.trim();
    if (!normalized || sending.value) return {};
    resolveScenario();
    active.value = true;
    sending.value = true;

    const localAbort = new AbortController();
    abortController.value = localAbort;
    const onAbort = () => localAbort.abort();
    opts?.signal?.addEventListener('abort', onAbort);

    addAuthorMessage('user', normalized);
    history.value.push({ role: 'user', content: normalized });

    const steps: ThoughtStep[] = [
      { key: 'sa_understand', icon: '⏳', title: '理解需求', text: '正在分析你的技能描述…' },
    ];
    opts?.onThought?.([...steps]);

    const progressTimer = window.setTimeout(() => {
      if (localAbort.signal.aborted) return;
      steps[0] = { ...steps[0], icon: '✅', text: '已理解技能目标' };
      steps.push({
        key: 'sa_design',
        icon: '⏳',
        title: '设计 Skill',
        text: '正在整理名称、Prompt 与 SOP…',
      });
      opts?.onThought?.([...steps]);
    }, 500);

    try {
      const result = await skillDialogue(
        smartDocAi.workspaceId || '0',
        {
          mode: mode.value,
          conversationId: conversationId.value || undefined,
          userMessage: normalized,
          history: history.value.slice(0, -1),
          sourceSkillId: sourceSkillId.value || undefined,
          category: category.value,
        },
        { signal: localAbort.signal },
      );
      if (localAbort.signal.aborted) {
        return { aborted: true };
      }
      if (result.conversationId) {
        conversationId.value = result.conversationId;
      }
      const reply = result.assistantReply || '已收到，请继续补充。';
      history.value.push({ role: 'assistant', content: reply });

      steps.forEach((s, i) => {
        steps[i] = { ...s, icon: '✅', text: s.text.replace(/正在|…/g, '').trim() || '已完成' };
      });
      if (result.phase === 'draft_ready') {
        steps.push({
          key: 'sa_ready',
          icon: '✅',
          title: '草稿就绪',
          text: '技能草稿已整理完成，可保存或开始使用',
        });
      } else {
        steps.push({
          key: 'sa_ask',
          icon: '✅',
          title: '继续补充',
          text: '仍需更多信息以完善技能',
        });
      }
      opts?.onThought?.([...steps]);
      const thoughtJson = thoughtStepsToJson(steps);

      if (result.phase === 'draft_ready' && result.draft?.name) {
        pendingDraft.value = result.draft;
        const assistantMsg = addAuthorMessage(
          'assistant',
          reply,
          buildDraftStructured(result.draft, 'pending') as unknown as Record<string, unknown>,
          thoughtJson,
        );
        pendingMessageId.value = assistantMsg.id;
      } else {
        pendingDraft.value = null;
        pendingMessageId.value = null;
        addAuthorMessage('assistant', reply, null, thoughtJson);
      }
      return {};
    } catch (e) {
      if (isAbortError(e) || localAbort.signal.aborted) {
        addAuthorMessage('assistant', '已终止本次任务。');
        return { aborted: true };
      }
      ElMessage.error(formatApiErrorMessage(e));
      addAuthorMessage('assistant', formatApiErrorMessage(e) || '对话失败，请稍后重试');
      return {};
    } finally {
      window.clearTimeout(progressTimer);
      opts?.signal?.removeEventListener('abort', onAbort);
      if (abortController.value === localAbort) {
        abortController.value = null;
      }
      sending.value = false;
    }
  }

  async function confirmSave(messageId?: string): Promise<DocumentSkillDetail | null> {
    if (!pendingDraft.value) {
      ElMessage.warning('暂无待保存的技能草稿');
      return null;
    }
    const draft = pendingDraft.value;
    void messageId;
    patchPendingStructured('saving');
    sending.value = true;
    try {
      if (mode.value === 'optimize' && sourceSkillId.value) {
        const updated = await updateDocumentSkillContent(sourceSkillId.value, {
          name: draft.name,
          category: draft.category,
          description: draft.description,
          promptText: draft.promptText,
          sopText: draft.sopText,
          inputSchemaJson: draft.inputSchemaJson,
          toolPermissionsJson: draft.toolPermissionsJson,
          outputContractJson: draft.outputContractJson,
        });
        patchPendingStructured('done');
        await smartDocAi.reloadSkills();
        smartDocAi.setSkillId(updated.id);
        ElMessage.success(`已更新技能：${updated.name}`);
        endSkillAuthoring();
        return updated;
      }
      const created = await createDocumentSkill(smartDocAi.workspaceId || '0', {
        code: draft.code,
        name: draft.name,
        category: draft.category || 'document_generation',
        description: draft.description,
        visibility: 'private',
        promptText: draft.promptText,
        sopText: draft.sopText,
        inputSchemaJson: draft.inputSchemaJson,
        toolPermissionsJson: draft.toolPermissionsJson,
        outputContractJson: draft.outputContractJson,
      });
      patchPendingStructured('done');
      await smartDocAi.reloadSkills();
      smartDocAi.setSkillId(created.id);
      ElMessage.success(`已保存到我的技能：${created.name}`);
      endSkillAuthoring();
      return created;
    } catch (e) {
      patchPendingStructured('pending');
      ElMessage.error(formatApiErrorMessage(e));
      return null;
    } finally {
      sending.value = false;
    }
  }

  /** 保存并返回技能，供「开始使用」创建新会话并引用 */
  async function startUsing(messageId?: string): Promise<DocumentSkillDetail | null> {
    return confirmSave(messageId);
  }

  function continueEditing(messageId?: string) {
    if (messageId && pendingMessageId.value === messageId) {
      patchPendingStructured('cancelled');
    } else if (pendingMessageId.value) {
      patchPendingStructured('cancelled');
    }
    pendingDraft.value = null;
    pendingMessageId.value = null;
    const tip = '好的，请继续说明要修改的地方。';
    addAuthorMessage('assistant', tip);
    history.value.push({ role: 'assistant', content: tip });
  }

  function cancelAuthoring() {
    cancelSending();
    if (pendingMessageId.value) {
      patchPendingStructured('cancelled');
    }
    endSkillAuthoring();
  }

  return {
    active,
    mode,
    conversationId,
    sourceSkillId,
    category,
    sending,
    pendingDraft,
    pendingMessageId,
    history,
    bindPanelContext,
    beginSkillAuthoring,
    endSkillAuthoring,
    sendUserMessage,
    confirmSave,
    startUsing,
    continueEditing,
    cancelAuthoring,
    cancelSending,
    isCreateSkillIntent,
  };
});
